import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  ActivationsApi,
  Configuration,
  EntitlementsApi,
  GetEntitlementsResponse,
} from "api";
import { getBasePath } from "common/getConfiguration";
import { openSnackbar } from "features/ui";
import { AxiosError } from "axios";
import { isError } from "lodash";
import { downloadFile } from "common/download";
import { createErrorObject } from "errors/createErrorObject";
import { Buffer } from "buffer";
import { getTranslationID } from "translation";
import { SoftwareState } from "./state";
import { PAGE_SIZE } from "./constants";

export const initialAsync = createAsyncThunk<
  SoftwareState,
  void,
  {
    // rejectした時の返却値の型
    rejectValue: {
      /* Empty Object */
    };
  }
>("software/initialAsync", async (args, thunkApi) => {
  // apiのConfigurationを取得する
  const basePath = getBasePath();

  const config = new Configuration({ basePath });
  try {
    // ソフトウェア一覧を取得するAPI呼び出し
    const entitlementsApi = new EntitlementsApi(config);
    const { data } = await entitlementsApi.getEntitlements(PAGE_SIZE, 0, "");

    return {
      apps: {
        loading: "Loaded",
        maxPage: data.maxPage,
        pageIndex: data.pageIndex,
        pageSize: PAGE_SIZE,
        searchText: "",
      },
      domains: {
        entitlements: {
          nowIndex: data.pageIndex,
          Entitlements: data.entitlements,
        },
      },
    };
  } catch (e) {
    if (isAxiosError(e)) {
      if (e.response?.status === 401) {
        return thunkApi.rejectWithValue({});
      }
    }
    thunkApi.dispatch(
      openSnackbar({
        level: "error",
        message: getTranslationID("common.message.serverInternalError"),
      })
    );
    return thunkApi.rejectWithValue({});
  }
});

export const updateAsync = createAsyncThunk<
  GetEntitlementsResponse,
  { pageSize: number; pageIndex: number; searchText: string },
  {
    // rejectした時の返却値の型
    rejectValue: {
      /* Empty Object */
    };
  }
>("software/updateAsync", async (args, thunkApi) => {
  // apiのConfigurationを取得する
  const basePath = getBasePath();
  const { pageSize, pageIndex, searchText } = args;

  const config = new Configuration({ basePath });
  try {
    // ソフトウェア一覧を取得するAPI呼び出し
    const entitlementsApi = new EntitlementsApi(config);
    const { data } = await entitlementsApi.getEntitlements(
      pageSize,
      pageIndex,
      searchText
    );
    return data;
  } catch (e) {
    if (isAxiosError(e)) {
      if (e.response?.status === 401) {
        return thunkApi.rejectWithValue({});
      }
    }
    thunkApi.dispatch(
      openSnackbar({
        level: "error",
        message: getTranslationID("common.message.serverInternalError"),
      })
    );
    return thunkApi.rejectWithValue({});
  }
});

export const activateTrialLicenseAsync = createAsyncThunk<
  { success: boolean },
  { pkId: string },
  {
    // rejectした時の返却値の型
    rejectValue: {
      /* Empty Object */
    };
  }
>("software/activateTrialLicenseAsync", async (args, thunkApi) => {
  // apiのConfigurationを取得する
  const basePath = getBasePath();
  const { pkId } = args;

  const config = new Configuration({ basePath });
  try {
    const activationsApi = new ActivationsApi(config);
    const { data } = await activationsApi.activateTrialLicense({
      pkId,
    });

    downloadFile("lservrc", data.licenseKey, "application/octet-stream");

    thunkApi.dispatch(
      openSnackbar({
        level: "info",
        message: getTranslationID("softwarePage.message.activateSuccess"),
      })
    );
    return { success: true };
  } catch (e) {
    const error = createErrorObject(e);
    if (error.errorCode === "E009999") {
      thunkApi.dispatch(
        openSnackbar({
          level: "error",
          message: getTranslationID("common.message.serverInternalError"),
        })
      );
    }
    return thunkApi.rejectWithValue({});
  }
});

export const activateLicenseAsync = createAsyncThunk<
  { success: boolean },
  { pkId: string; fileName: string; fingerPrint: string },
  {
    // rejectした時の返却値の型
    rejectValue: {
      /* Empty Object */
    };
  }
>("software/activateLicenseAsync", async (args, thunkApi) => {
  // apiのConfigurationを取得する
  const basePath = getBasePath();
  const { pkId, fileName, fingerPrint } = args;
  const escapedFingerPrint = fingerPrint.replace(/\r?\n|\t/g, "");
  const encodedFingerPrint = Buffer.from(escapedFingerPrint).toString("base64");

  const config = new Configuration({ basePath });
  try {
    const activationsApi = new ActivationsApi(config);
    const { data } = await activationsApi.activateLicense({
      pkId,
      fileName,
      fingerPrint: encodedFingerPrint,
    });

    downloadFile("lservrc", data.licenseKey, "application/octet-stream");

    thunkApi.dispatch(
      openSnackbar({
        level: "info",
        message: getTranslationID("softwarePage.message.activateSuccess"),
      })
    );
    return { success: true };
  } catch (e) {
    const error = createErrorObject(e);

    // 送信するファイルが巨大すぎる場合は不正なファイルなのでFingerPrintのエラーとして処理する
    if (error.statusCode === 413) {
      thunkApi.dispatch(
        openSnackbar({
          level: "error",
          message: getTranslationID("softwarePage.message.fingerPrintError"),
        })
      );
      return thunkApi.rejectWithValue({});
    }

    const msg =
      error.errorCode === "E010101"
        ? getTranslationID("softwarePage.message.fingerPrintError")
        : getTranslationID("common.message.serverInternalError");
    thunkApi.dispatch(
      openSnackbar({
        level: "error",
        message: msg,
      })
    );
    return thunkApi.rejectWithValue({});
  }
});
export const transferLicenseAsync = createAsyncThunk<
  { success: boolean },
  { email: string; eId: string },
  {
    // rejectした時の返却値の型
    rejectValue: {
      /* Empty Object */
    };
  }
>("software/transferLicenseAsync", async (args, thunkApi) => {
  // apiのConfigurationを取得する
  const basePath = getBasePath();
  const { email, eId } = args;

  const config = new Configuration({ basePath });
  try {
    const entitlementsApi = new EntitlementsApi(config);

    await entitlementsApi.updateEntitlement({
      email,
      eId,
    });

    thunkApi.dispatch(
      openSnackbar({
        level: "success",
        message: getTranslationID(
          "softwarePage.message.transferLicenseSuccess"
        ),
      })
    );
    return { success: true };
  } catch (e) {
    const error = createErrorObject(e);

    // Sentinelに存在しないメールアドレスの場合は別に処理する
    if (error.errorCode === "E010102") {
      thunkApi.dispatch(
        openSnackbar({
          level: "error",
          message: getTranslationID("softwarePage.message.invalidEmailError"),
        })
      );
      return thunkApi.rejectWithValue({});
    }

    thunkApi.dispatch(
      openSnackbar({
        level: "error",
        message: getTranslationID("common.message.serverInternalError"),
      })
    );
    return thunkApi.rejectWithValue({});
  }
});

const isAxiosError = (arg: unknown): arg is AxiosError => {
  if (!isError(arg)) {
    return false;
  }
  const errorValue = arg as AxiosError;
  return errorValue.isAxiosError;
};
